import type { AxiosError } from "axios";
import Axios from "axios";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import styled from "styled-components/macro";
import useSWR from "swr";
import { NumberParam, useQueryParams } from "use-query-params";
import { useAuthContext } from "../../../../components/Auth";
import type { BlockLeadFormData } from "../../../../components/Buttons/Buttons";
import {
  CancelButton,
  LinkButtonLargeV2,
  PrimaryButtonLarge,
  PrimaryButtonMedium,
} from "../../../../components/Buttons/Buttons";
import { ContactInfoBlockSmall } from "../../../../components/ContactInfoBlockSmall/ContactInfoBlockSmall";
import { CreateNewTenantFormContainer } from "../../../../components/CreateNewTenantForm/CreateNewTenantFormContainer";
import { CreateNewUser } from "../../../../components/CreateNewUser/CreateNewUser";
import { LeadAssignmentInfo } from "../../../../components/LeadAssignmentInfo";
import { DropDownButton } from "../../../../components/DropDownButton/DropDownButton";
import { ErrorPlaceholder } from "../../../../components/Error";
import { WarningMessageBox } from "../../../../components/Form/Form";
import { CaretLeft, WarningIcon } from "../../../../components/Icons/Icons";
import { IDDesktop } from "../../../../components/IDs/IDs";
import { InfoBlockExtraSmall } from "../../../../components/InfoBlocks/InfoBlocks";
import { HeaderLeft, HeaderRight } from "../../../../components/Layout/Layout";
import { Loader } from "../../../../components/Loader/Loader";
import { MultiLineBlock } from "../../../../components/LocationsList/LocationsList";
import { Modal } from "../../../../components/Modal/Modal";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { SlideOut } from "../../../../components/SlideOut/SlideOut";
import { StatusRight } from "../../../../components/Status/Status";
import { TransactionItem } from "../../../../components/TransactionItem/TransactionItem";
import { TransactionItemInfoBlock } from "../../../../components/TransactionItem/TransactionItemInfoBlock";
import { TransactionItemTopLeft } from "../../../../components/TransactionItem/TransactionItemTopLeft";
import { H3 } from "../../../../components/Typography/Typography";
import { endpoints } from "../../../../endpoints";
import {
  Card,
  ContentWrapper,
  FullWidthHorizontalSeparator,
  PageHeader,
  PageTitle,
  PageWrapper,
} from "../../../../layout/portalPageLayout";
import { WideDetails } from "../../../../layout/shared/DetailPageLayout/DetailPageLayout";
import type {
  BlockLeadData,
  IAddress,
  Lead,
  LeadModalType,
  LeadRejectionArgsSchema,
  LeadTenantType,
  LocationState,
  Tenant,
  UserTypeChip,
} from "../../../../types/types";
import { formateInternationalPhoneNumber } from "../../../../util/phone";
import { useRoutePath } from "../../../../util/Routing";
import { useAuthenticatedUser } from "../../../../hooks/useAuthenticatedUser";
import {
  formatDateTime,
  makeUrlWithParams,
  useInAppNotifications,
  useStoreState,
} from "../../../../util/util";
import type { blockLeadLabels } from "../../../admin/SellerAdmin/SellerAdminSampleRequestLeadDetail/SellerAdminSampleRequestLeadDetail";
import {
  ActiveWarningMessage,
  NotificationContainer,
} from "../../SellerQuoteDetailPage/SellerQuoteDetailPageContent";
import { AssignLeadToUser } from "../AssignLeadToUser";
import { AssignLeadSubscribers } from "../AssignLeadSubscribers";
import { BlockLeadToUserModal } from "../BlockLeadToUserModal";
import type { HandleLeadNoteSubmitProps } from "../LeadNotes";
import { LeadNotes } from "../LeadNotes";
import { LeadRejectionModal } from "../LeadRejectionModal";
import { getLeadStatusDetails } from "../leadsUtilities";
import { LeadToBlockModal } from "../LeadToBlockModal";
import type { IForwardLead } from "../ForwardToDistributor";
import { ForwardToDistributor } from "../ForwardToDistributor";
import type { FollowedExternallyFormValues } from "../FollowedExternallyModal";
import { FollowedExternallyModal } from "../FollowedExternallyModal";
import { AlchemistInsightsBanner } from "../../../../components/AlchemistInsightsBanner/AlchemistInsightsBanner";
import { LeadIntelDisplay } from "../LeadIntelDisplay";
import { spacingTokens } from "../../../../designTokens";

const ButtonSection = styled.div`
  display: flex;
  justify-content: space-between;
`;

const EmailContainer = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 250px);
  grid-gap: 30px;
`;

export const RightButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: row;
`;

/**
 * A shared page component that shows details for a single quote request
 * that came from a guest user that does not have an account yet (a new
 * lead).
 *
 * Used for seller admin users.
 */
export const QuoteRequestLeadDetailPage = () => {
  const user = useAuthenticatedUser();
  // The role can be SELLER_ADMIN.
  const { roleIsSomeKindOfSeller, roleIsDistributorAdmin } = useAuthContext();
  const {
    storefront_metadata: { tenant_type },
    storefront_id,
  } = useStoreState();

  const userIsProducerSeller =
    roleIsSomeKindOfSeller && tenant_type === "Producer";

  const { lead_id } = useParams<{ lead_id: string }>();

  const [query] = useQueryParams({
    offset: NumberParam,
    perPage: NumberParam,
  });

  const {
    data: lead,
    error,
    mutate: mutateLead,
  } = useSWR<Lead, AxiosError>(
    `/v1/storefronts/${storefront_id}/leads/${lead_id}`
  );

  const { t } = useTranslation();

  const history = useHistory();
  const location = useLocation();
  const { adminPath } = useRoutePath();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const [tenantTypeToBeCreated, setTenantTypeToBeCreated] =
    useState<"Buyer" | "Distributor">("Buyer");

  const [showLeadRejectionModal, setShowLeadRejectionModal] = useState(false);
  const closeLeadRejectionModal = () => setShowLeadRejectionModal(false);
  const openLeadRejectionModal = () => setShowLeadRejectionModal(true);

  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const closeCreateCustomerModal = () => setShowCreateCustomerModal(false);
  const openCreateCustomerModal = () => setShowCreateCustomerModal(true);

  const [showLeadAssignmentModal, setShowLeadAssignmentModal] = useState(false);
  const closeLeadAssignmentModal = () => setShowLeadAssignmentModal(false);
  const openLeadAssignmentModal = () => setShowLeadAssignmentModal(true);

  const [showSubscribersModal, setShowSubscribersModal] = useState(false);

  const [showAddToCustomerModal, setShowAddToCustomerModal] = useState(false);

  const [showConvertLeadModal, setShowConvertLeadModal] = useState(false);
  const openConvertToLeadModal = () => setShowConvertLeadModal(true);
  const closeConvertToLeadModal = () => setShowConvertLeadModal(false);

  const [dropDownName, setDropDownName] = useState("");

  const [isForwardToDistributorOpen, setIsForwardToDistributorOpen] =
    useState(false);
  const openForwardToDistributor = () => setIsForwardToDistributorOpen(true);
  const closeForwardToDistributor = () => setIsForwardToDistributorOpen(false);

  const [showFollowedExternallyDialog, setShowFollowedExternallyDialog] =
    useState(false);
  const openFollowedExternallyModal = () =>
    setShowFollowedExternallyDialog(true);
  const closeFollowedExternallyModal = () =>
    setShowFollowedExternallyDialog(false);

  const [showLeadIntelSlideOut, setShowLeadIntelSlideOut] = useState(false);
  const openLeadIntelSlideOut = () => setShowLeadIntelSlideOut(true);
  const closeLeadIntelSlideOut = () => setShowLeadIntelSlideOut(false);

  const { mutateNotifications } = useInAppNotifications(storefront_id, user);
  useEffect(() => {
    if (lead) {
      mutateNotifications();
    }
  }, [lead, mutateNotifications]);

  const { data: blockLeadData, mutate: mutateBlockLeadData } = useSWR<
    BlockLeadData,
    AxiosError
  >(`/v1/storefronts/${storefront_id}/leads/${lead_id}/blocks`);

  const { data: spamStatusData } = useSWR(
    `/v1/storefronts/${storefront_id}/leads/${lead_id}/spam-status`
  );

  const isGlobalLeadSpam = spamStatusData?.is_spam;
  const isCurrentLeadSpam = blockLeadData?.items?.some(
    (block) => block.is_spam
  );
  const isActionDisable: boolean = !!blockLeadData?.items?.length;

  // For when the user is a producer seller admin and for forwarded leads:
  // The producer's lead has a `children` property that contains the
  // distributor's copy of the lead. We use the seller ID from the
  // distributor's copy of the lead to get the contact details for the
  // distributor tenant, so we can display those in the "Forwarded to"
  // section.
  const childLead = lead?.children?.[0];
  const childLeadSellerId = childLead?.seller_id;

  const { data: forwardedToTenant } = useSWR<Tenant>(
    userIsProducerSeller && childLeadSellerId
      ? endpoints.v1_storefronts_id_tenants_id(storefront_id, childLeadSellerId)
      : lead?.status === "Added as User"
      ? endpoints.v1_storefronts_id_tenants_id(
          storefront_id,
          lead.parent_customer_id
        )
      : null
  );
  const forwardedToContact = forwardedToTenant?.primary_contact;

  const hasTransactionData = Boolean(
    lead?.address && lead?.billing_address && lead?.required_eta
  );

  const onConvertToCustomer = (tenantId: string) => {
    closeCreateCustomerModal();

    history.push(
      `${adminPath}/${
        tenantTypeToBeCreated === "Buyer" ? "tenants" : "distributors"
      }/${tenantId}`
    );
  };

  const isLeadRejected = lead?.status === "Rejected";

  const handleRejectQuote = async (messages: LeadRejectionArgsSchema) => {
    const action = isLeadRejected ? "restore" : "reject";
    const successMessage = isLeadRejected
      ? t("Lead restore successfully")
      : t("Lead rejected successfully");
    const errorMessage = isLeadRejected
      ? t("There was an error restoring the quote, please try again.")
      : t("There was an error rejecting the quote, please try again.");

    try {
      const response = await Axios.post(
        `/v1/tenants/${user.tenant_id}/leads/${lead_id}/${action}`,
        messages
      );
      if (response.status === 201) {
        notifySuccess(successMessage);
        mutateLead();
        closeLeadRejectionModal();
      }
    } catch (error) {
      notifyError(errorMessage, {
        error,
      });
    }
  };

  const onLeadNoteSubmit = async ({
    note,
    onSuccess,
  }: HandleLeadNoteSubmitProps) => {
    try {
      await Axios.post(
        `/v1/storefronts/${storefront_id}/leads/${lead_id}/notes`,
        {
          type: "quote_request",
          message: note,
        }
      );
      onSuccess();
      mutateLead();
      notifySuccess(t("note added successfully"));
    } catch (error) {
      notifyError(t("There was an error adding the note"));
    }
  };

  const onSubscribersSuccess = () => {
    setShowSubscribersModal(false);
    mutateLead();
    notifySuccess(t("Subscribers updated successfully"));
  };

  const onSubscribersError = (error: unknown) => {
    const errorMessage = (error as AxiosError)?.response?.data?.message;
    notifyError(
      errorMessage
        ? errorMessage
        : t("There was an error updating subscribers"),
      {
        error,
      }
    );
  };

  const leadSubscribersUrl = endpoints.v2_storefronts_id_leads_id_subscribers(
    storefront_id,
    lead_id
  );

  const openAddUserToCustomerModal = () => setShowAddToCustomerModal(true);
  const closeAddUserToCustomerModal = () => setShowAddToCustomerModal(false);

  const requestedEta = lead?.required_eta
    ? moment(lead.required_eta).format("MMMM DD, YYYY")
    : undefined;

  const getBackLinkState = () => {
    const listState = (location.state as LocationState)?.listState;
    return listState ? { listState } : undefined;
  };

  const isModificationDisabled =
    isActionDisable ||
    lead?.status === "Sent to Distributor" ||
    lead?.status === "Rejected by Distributor";

  const getBackLinkParams = () => {
    const { tabIndex, offset, perPage } =
      (location.state as LocationState)?.listState || {};
    return {
      tab: tabIndex !== undefined ? tabIndex : undefined,
      offset: offset !== undefined ? offset : undefined,
      perPage: perPage !== undefined ? perPage : undefined,
    };
  };

  const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const state = getBackLinkState();
    const params = getBackLinkParams();
    const search = makeUrlWithParams("", params);
    history.push({
      pathname: `${adminPath}/leads`,
      search,
      state,
    });
  };

  const PageWrapperEtc = ({ children }: { children: React.ReactNode }) => (
    <PageWrapper style={{ maxWidth: "932px" }}>
      <Link
        to={makeUrlWithParams(`${adminPath}/leads`, {
          offset: query.offset,
          perPage: query.perPage,
        })}
        onClick={handleBackClick}
      >
        <LinkButtonLargeV2>
          <CaretLeft fill="currentColor" />
          {t("Leads")}
        </LinkButtonLargeV2>
      </Link>
      {children}
    </PageWrapper>
  );

  const maxEmailLength = 33;
  const isEmailTooLong = lead && lead.buyer_email?.length > maxEmailLength;

  const handleBlockLead = async (
    data: BlockLeadFormData,
    setShow: (show: boolean) => void
  ) => {
    const blockLeadLabels: blockLeadLabels = {
      block_domain: "domain",
      block_email: "email",
      block_phone_number: "phone",
    };

    const store: string[] = Object.entries(data)
      .filter(([key, value]) => {
        const excludedKeys = ["report_spam", "reasonForBlockLead"];
        return !excludedKeys.includes(key) && value;
      })
      .map(([key]) => blockLeadLabels[key as keyof blockLeadLabels]);

    try {
      const response = await Axios.post(
        `/v1/storefronts/${storefront_id}/leads/${lead_id}/blocks`,
        {
          block_types: store,
          is_spam: data?.report_spam,
          reason: data?.reasonForBlockLead,
        }
      );
      if (response?.status === 201) {
        notifySuccess(t("Block lead added successfully"));
        mutateBlockLeadData();
        mutateLead();
        setShow(false);
      }
    } catch (error) {
      notifyError(t("There was an error adding the block lead"));
    }
  };

  const modalHandlers: Record<LeadModalType, () => void> = {
    convertToNew: openCreateCustomerModal,
    addToExisting: openAddUserToCustomerModal,
    forwardToDistributor: openForwardToDistributor,
    followedExternally: openFollowedExternallyModal,
  };

  const handleModalOpening = (type: LeadModalType) => {
    if (isGlobalLeadSpam) {
      openConvertToLeadModal();
      return;
    }

    const handler = modalHandlers[type];
    if (handler) handler();
  };

  const modalConfigMap: Record<
    string,
    {
      tenantType?: LeadTenantType;
      dropDownName: LeadModalType;
    }
  > = {
    [t("Followed Externally")]: {
      dropDownName: "followedExternally",
    },
    [t("Forward to Distributor")]: {
      dropDownName: "forwardToDistributor",
    },
    [t("Convert to customer")]: {
      tenantType: "Buyer",
      dropDownName: "convertToNew",
    },
    [t("Convert to distributor")]: {
      tenantType: "Distributor",
      dropDownName: "convertToNew",
    },
    [t("Add to existing customer")]: {
      tenantType: "Buyer",
      dropDownName: "addToExisting",
    },
    [t("Add to existing distributor")]: {
      tenantType: "Distributor",
      dropDownName: "addToExisting",
    },
  };

  const handleDropdownClick = (item: string) => {
    const config = modalConfigMap[item];
    if (!config) return;

    const { tenantType, dropDownName } = config;

    if (tenantType) {
      setTenantTypeToBeCreated(tenantType);
    }

    setDropDownName(dropDownName);
    handleModalOpening(dropDownName);
  };

  const handleConvertToLead = () => {
    setShowConvertLeadModal(false);
    if (dropDownName) {
      modalHandlers[dropDownName as LeadModalType]?.();
    }
  };

  const handleForwardToDistributor = async (
    data: IForwardLead
  ): Promise<void> => {
    try {
      const response = await Axios.post(
        endpoints.v2_storefronts_storefront_id_leads_lead_id_forward(
          storefront_id,
          lead_id
        ),
        {
          distributor_id: data.distributor.value,
          message: data.reasonForForwardLead,
          type: "quote_request",
        }
      );

      if (response?.status === 201) {
        notifySuccess(t("Forwarded successfully"));
        mutateLead();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    } finally {
      closeForwardToDistributor();
    }
  };

  const handleCreateNewUserSuccess = () => {
    if (roleIsDistributorAdmin) {
      mutateLead();
    }
    closeAddUserToCustomerModal();
  };

  const handleSkipAdding = async () => {
    try {
      const response = await Axios.patch(
        endpoints.v1_storefronts_storefront_id_leads_lead_id_accept(
          storefront_id,
          lead_id
        )
      );

      if (response?.status === 201) {
        notifySuccess(t("Lead request accepted successfully"));
        mutateLead();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    } finally {
      closeAddUserToCustomerModal();
    }
  };

  const handleAcceptLead = () => {
    handleDropdownClick("Add to existing distributor");
  };

  const handleFollowedExternally = async (
    data: FollowedExternallyFormValues
  ) => {
    const channels = ["email", "phone", "fax", "text"];
    const selectedChannelsString = channels
      .filter((key) => data[key] === true)
      .toString();

    try {
      const response = await Axios.post(
        endpoints.v2_storefronts_storefront_id_leads_lead_id_followed_externally(
          storefront_id,
          lead_id
        ),
        {
          followed_externally_via: selectedChannelsString,
          notes: data.reasonForFollowedExternally,
        }
      );
      if (response?.status === 201) {
        notifySuccess(t("Lead marked as followed up externally"));
        mutateLead();
        closeFollowedExternallyModal();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  if (error) {
    return (
      <PageWrapperEtc>
        <ErrorPlaceholder
          message={t("There was an error loading the quote lead")}
        />
      </PageWrapperEtc>
    );
  }
  if (!lead && !error) {
    return (
      <PageWrapperEtc>
        <Loader isLoading={true} />
      </PageWrapperEtc>
    );
  }
  if (lead) {
    const { text: statusText, color: statusColor } = getLeadStatusDetails(
      lead.status,
      t
    );

    const onLeadAssignmentSuccess = (state: "unassigned" | "assigned") => {
      closeLeadAssignmentModal();
      mutateLead();
      state === "assigned"
        ? notifySuccess(t("Lead assigned successfully"))
        : notifySuccess(t("Lead unassigned successfully"));
    };

    const onLeadAssignmentFailure = (error: unknown) => {
      notifyError(t("There was an error assigning the lead"), { error });
    };

    const leadAssignmentUrl = `/v2/storefronts/${storefront_id}/leads/${lead_id}/assign`;

    const userTypeChips: UserTypeChip[] =
      tenantTypeToBeCreated === "Buyer"
        ? [{ name: t("Admin"), id: "Buyer Admin" }]
        : [{ name: t("Distributor"), id: "Distributor Admin" }];

    const shouldShowAssigneeSection =
      lead.status !== "Sent to Distributor" &&
      lead.status !== "Rejected by Distributor" &&
      lead.status !== "Accepted by Distributor";

    const titleString = isLeadRejected
      ? t("Are you sure you want to restore this Quote Request?")
      : t("Are you sure you want to reject this Quote Request?");

    const dropdownItems = [
      t("Convert to customer"),
      t("Convert to distributor"),
      t("Add to existing customer"),
      t("Add to existing distributor"),
      t("Forward to Distributor"),
      t("Followed Externally"),
    ];

    return (
      <PageWrapperEtc>
        <SlideOut
          title={
            roleIsDistributorAdmin ? t("Assign") : t("Assign Lead To User")
          }
          show={showLeadAssignmentModal}
          closeFlyout={closeLeadAssignmentModal}
        >
          <AssignLeadToUser
            url={leadAssignmentUrl}
            onSuccess={onLeadAssignmentSuccess}
            onError={onLeadAssignmentFailure}
            prefilledUser={lead?.assignee}
            distributorId={user.tenant_id}
          />
        </SlideOut>
        <SlideOut
          show={showSubscribersModal}
          closeFlyout={() => setShowSubscribersModal(false)}
          title={t("Assign Lead Subscriber(s)")}
        >
          <AssignLeadSubscribers
            url={leadSubscribersUrl}
            initialSubscribers={lead.subscribers || []}
            onSuccess={onSubscribersSuccess}
            onError={onSubscribersError}
          />
        </SlideOut>
        <PageHeader>
          <HeaderLeft>
            <PageTitle>{t("Quote Request")}</PageTitle>
            {lead.number && <IDDesktop>{lead.number}</IDDesktop>}
          </HeaderLeft>
          <HeaderRight>
            <StatusRight color={statusColor} text={statusText} />
          </HeaderRight>
        </PageHeader>
        <LeadAssignmentInfo
          assignee={lead.assignee}
          assignmentDate={lead.assignment_date}
          subscribers={lead.subscribers}
          subscribersUpdatedOn={lead.subscribers_updated_on}
          subscriber_assignment_type={lead.subscriber_assignment_type}
          shouldShowAssigneeSection={shouldShowAssigneeSection}
          onOpenLeadAssignmentModal={openLeadAssignmentModal}
          onOpenSubscribersModal={() => setShowSubscribersModal(true)}
          isActionDisabled={isActionDisable}
          isModificationDisabled={isModificationDisabled}
        />
        <FullWidthHorizontalSeparator />

        <ContentWrapper
          style={{ margin: `45px 0 ${spacingTokens.spacing_2xl} 0` }}
        >
          {lead.lead_intel && (
            <AlchemistInsightsBanner onViewDetails={openLeadIntelSlideOut} />
          )}
          <Card key="Quote-request-lead-detail">
            <H3>{t("Customer Details")}</H3>
            <Details>
              <div>
                <InfoBlockExtraSmall
                  header={t("Requested By")}
                  content={
                    <MultiLineBlock>
                      {/* All of the property names are different for every lead.. 
                        Not much point in having 4 different utility functions just to export them and use them once. */}
                      <div>
                        {`${lead.buyer_first_name} ${lead.buyer_last_name}`}
                      </div>
                      <div>{lead.buyer_company_name}</div>
                      <div>
                        {isEmailTooLong ? (
                          <>
                            <span data-tip={lead.buyer_email}>
                              {lead.buyer_email.slice(0, maxEmailLength)}...
                            </span>
                            <ReactTooltip effect="solid" />
                          </>
                        ) : (
                          lead.buyer_email
                        )}
                      </div>
                      <div>
                        <a href={`tel:${lead.buyer_phone_number}`}>
                          {formateInternationalPhoneNumber(
                            lead.buyer_phone_number
                          )}
                        </a>
                      </div>
                    </MultiLineBlock>
                  }
                />
              </div>
              <div>
                <InfoBlockExtraSmall
                  header={t("Date Received")}
                  content={formatDateTime(lead.created_at)}
                />
                {lead.status === "Added as User" && forwardedToContact && (
                  <div style={{ marginTop: "5px" }}>
                    <InfoBlockExtraSmall
                      header={t("User Added to")}
                      content={forwardedToTenant.name}
                    />
                  </div>
                )}
              </div>
              {lead.status === "Forwarded" &&
                forwardedToTenant &&
                forwardedToContact && (
                  <div>
                    <InfoBlockExtraSmall
                      header={t("Forwarded to")}
                      content={
                        <MultiLineBlock style={{ wordWrap: "break-word" }}>
                          <div>{forwardedToTenant.name}</div>
                          <div>
                            {`${forwardedToContact.first_name} ${forwardedToContact.last_name}`}
                          </div>
                          <EmailContainer>
                            {forwardedToContact.email_address}
                          </EmailContainer>
                          <div>
                            <a href={`tel:${forwardedToContact.phone_number}`}>
                              {formateInternationalPhoneNumber(
                                forwardedToContact.phone_number
                              )}
                            </a>
                          </div>
                        </MultiLineBlock>
                      }
                    />
                  </div>
                )}
            </Details>

            {(isCurrentLeadSpam || isGlobalLeadSpam) && (
              <NotificationContainer>
                <WarningIcon width={18} height={18} />
                <ActiveWarningMessage>
                  {t(
                    `This lead may be spam. Please review the details carefully before accepting this lead.`
                  )}
                </ActiveWarningMessage>
              </NotificationContainer>
            )}

            <H3>{t("Quote Request Details")}</H3>
            {hasTransactionData && (
              <WideDetails>
                <ContactInfoBlockSmall
                  address={lead.address as IAddress}
                  header={t("Ship to")}
                  testid={"address"}
                />
                <ContactInfoBlockSmall
                  address={lead!.billing_address}
                  header={t("Bill to")}
                  testid={"billing-address"}
                />
                <InfoBlockExtraSmall
                  header={t("Shipment ETA")}
                  content={requestedEta}
                />
              </WideDetails>
            )}
            {lead.cart.items.map((item: any, index) => (
              <React.Fragment key={item.id + index}>
                <TransactionItem
                  topLeft={<TransactionItemTopLeft item={item} index={index} />}
                  topRightCol2={
                    <TransactionItemInfoBlock
                      main={`${item.total_volume} ${
                        item.packaging_unit?.name ?? ""
                      }`}
                      lines={
                        !!item.packaging_type?.name
                          ? [item.packaging_type?.name]
                          : undefined
                      }
                      alignRight
                    />
                  }
                  hideSeparatorLine
                />
              </React.Fragment>
            ))}
            {/*
                  The buttons/actions to offer by user role and status:

                  Producer Seller Admin
                    "New" -> convert, send, reject
                    "Forwarded" -> no buttons
                    "Converted" -> no buttons
                    "Accepted" -> no buttons
                    "Rejected" -> no buttons
                    "Lead Declined"; -> convert, reject (not send)
                */}
            {(roleIsSomeKindOfSeller || roleIsDistributorAdmin) &&
              (lead.status === "New" || lead.status === "blocked") && (
                <>
                  {lead.is_existing_customer && (
                    <WarningMessageBox>
                      <WarningIcon width={18} />
                      {t(
                        `This lead may be a user of an existing customer, {{companyName}}. Click Add to Existing Customer to add this lead as a user to {{companyName}}.`,
                        {
                          companyName: lead.company_name,
                        }
                      )}
                    </WarningMessageBox>
                  )}

                  <ButtonSection
                    style={{
                      marginBottom: spacingTokens.spacing_4xl,
                      marginTop: spacingTokens.spacing_5xl,
                    }}
                  >
                    <div>
                      <CancelButton
                        style={{ paddingLeft: spacingTokens.spacing_none }}
                        onClick={openLeadRejectionModal}
                        disabled={isActionDisable}
                      >
                        {t("Reject This Lead")}
                      </CancelButton>
                      {!roleIsDistributorAdmin && (
                        <LeadToBlockModal
                          handleBlockLead={handleBlockLead}
                          blockLeadData={blockLeadData}
                        />
                      )}
                    </div>
                    <RightButtonContainer>
                      {roleIsDistributorAdmin ? (
                        <PrimaryButtonMedium
                          style={{ height: "45px" }}
                          onClick={handleAcceptLead}
                        >
                          {t("Accept")}
                        </PrimaryButtonMedium>
                      ) : (
                        <DropDownButton
                          items={dropdownItems}
                          textLeft={t("Accept")}
                          clickHandler={(item: string) => {
                            handleDropdownClick(item);
                          }}
                          isDisabled={isActionDisable}
                          style={{ padding: "11px 50px" }}
                        />
                      )}
                    </RightButtonContainer>

                    {showConvertLeadModal && (
                      <BlockLeadToUserModal
                        showConvertLeadModal={showConvertLeadModal}
                        handleCancel={closeConvertToLeadModal}
                        handleConvertToLead={handleConvertToLead}
                        setShowConvertLeadModal={setShowConvertLeadModal}
                      />
                    )}
                  </ButtonSection>

                  <Modal
                    closeModal={closeAddUserToCustomerModal}
                    show={showAddToCustomerModal}
                  >
                    <CreateNewUser
                      tenantId={user.tenant_id}
                      userTypeChips={userTypeChips}
                      defaultUserType={userTypeChips[0].id}
                      leadToUserData={{
                        firstname: lead.buyer_first_name,
                        lastname: lead.buyer_last_name,
                        email_address: lead.buyer_email,
                        phone_number: lead.buyer_phone_number,
                        lead_id: lead.id,
                        existing_customer: {
                          company_name: lead.company_name,
                          customer_id: lead.customer_id,
                        },
                        address: lead.address,
                      }}
                      tenantTypeToFetch={tenantTypeToBeCreated}
                      userFormType="add_to_Existing_customer"
                      onSuccess={handleCreateNewUserSuccess}
                      showShippingAddress={true}
                      isLeadForwarded={roleIsDistributorAdmin}
                      handleSkipAdding={handleSkipAdding}
                      onCancel={closeAddUserToCustomerModal}
                    />
                  </Modal>
                </>
              )}
            {userIsProducerSeller && lead.status === "Lead Declined" && (
              <ButtonSection style={{ marginTop: "30px" }}>
                <CancelButton onClick={openLeadRejectionModal}>
                  {t("Reject This Lead")}
                </CancelButton>
                <PrimaryButtonLarge onClick={openCreateCustomerModal}>
                  {t("Convert to New Customer")}
                </PrimaryButtonLarge>
              </ButtonSection>
            )}
            {isLeadRejected && (
              <PrimaryButtonMedium
                style={{
                  marginTop: "30px",
                }}
                onClick={openLeadRejectionModal}
              >
                {t("Restore to New")}
              </PrimaryButtonMedium>
            )}
            <Modal
              closeModal={closeCreateCustomerModal}
              show={showCreateCustomerModal}
            >
              <CreateNewTenantFormContainer
                onSuccess={onConvertToCustomer}
                lead={lead}
                sellerTenantId={user.tenant_id}
                type={tenantTypeToBeCreated}
                onCancel={closeCreateCustomerModal}
              />
            </Modal>
          </Card>
          <LeadRejectionModal
            show={showLeadRejectionModal}
            closeModal={closeLeadRejectionModal}
            onFormSubmit={handleRejectQuote}
            titleString={titleString}
            isLeadRejected={isLeadRejected}
          />
          <SlideOut
            show={isForwardToDistributorOpen}
            closeFlyout={closeForwardToDistributor}
            title={t("Forward to Distributor")}
          >
            <ForwardToDistributor
              handleForwardToDistributor={handleForwardToDistributor}
            />
          </SlideOut>
          <FollowedExternallyModal
            showModal={showFollowedExternallyDialog}
            closeModal={closeFollowedExternallyModal}
            handleFollowedExternally={handleFollowedExternally}
          />
          {lead.internal_notes && (
            <LeadNotes
              internal_notes={lead.internal_notes}
              handleSubmit={onLeadNoteSubmit}
            />
          )}
        </ContentWrapper>
        <SlideOut
          title={t("Lead Intelligence")}
          show={showLeadIntelSlideOut}
          closeFlyout={closeLeadIntelSlideOut}
        >
          {lead.lead_intel && (
            <LeadIntelDisplay leadIntel={lead.lead_intel} lead={lead} />
          )}
        </SlideOut>
      </PageWrapperEtc>
    );
  }
  // Should never happen, but TypeScript doesn't know that.
  return null;
};
